import React, { useEffect, useState, useMemo } from 'react'
import {
  Search,
  Plus,
  ExternalLink,
  Filter,
  Edit2,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  Info
} from 'lucide-react'
import { Card, StatusBadge, DateRangePicker } from '../ui'
import Tooltip from '../ui/Tooltip'
import usePartnerStore from '../../stores/partnerStore'
import usePartnerCategoryStore from '../../stores/partnerCategoryStore'
import usePartnerTierStore from '../../stores/partnerTierStore'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'
import usePermissionsStore from '../../stores/permissionsStore'
import { useToast } from '../../contexts/ToastContext'
import AddPartnerDrawer from './AddPartnerDrawer'
import EditPartnerDrawer from './EditPartnerDrawer'
import PartnerDetailDrawer from './PartnerDetailDrawer'
import { Partner } from '../../types/partner'
import { getPartnerLogoUrl } from '../../services/partnerService'
import ValueDeliveredBar, { valueDelivered, SOCIAL_COLOR, BROADCAST_COLOR } from './ValueDeliveredBar'

interface DateRange {
  start: string
  end: string
}

type SortField = 'name' | 'value'
type SortOrder = 'asc' | 'desc'

const PartnersList: React.FC = () => {
  const { showError } = useToast()
  const { partners, pagination, loading, error, loadPartners } = usePartnerStore()
  const { categories, loadCategories } = usePartnerCategoryStore()
  const { tiers, loadTiers } = usePartnerTierStore()
  const { selectedCustomer } = useGlobalCustomerStore()
  const { permissions } = usePermissionsStore()

  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [tierFilter, setTierFilter] = useState<string>('all')
  const [sortBy, setSortBy] = useState<SortField>('name')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false)
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false)
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false)
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null)
  const [selectedPeriod, setSelectedPeriod] = useState<'day' | 'week' | 'month' | '60days' | '90days' | 'custom'>('custom')
  const [customDateRange, setCustomDateRange] = useState<DateRange | null>(() => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 364)
    return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] }
  })

  // Load categories and tiers on mount
  useEffect(() => {
    loadCategories(true)
    loadTiers(true)
  }, [loadCategories, loadTiers])

  // Debounce search input to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      // Reset to first page when search changes
      if (searchQuery !== debouncedSearch) {
        setCurrentPage(1)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  // Handle date range change from DateRangePicker
  const handleDateRangeChange = (range: DateRange | null, period: string) => {
    if (period === 'custom' && range) {
      setSelectedPeriod('custom')
      setCustomDateRange(range)
    } else {
      setSelectedPeriod(period as typeof selectedPeriod)
      setCustomDateRange(null)
    }
    setCurrentPage(1)
  }

  // Load partners with server-side search
  useEffect(() => {
    if (!selectedCustomer?.id) return

    // Calculate actual date range based on period
    let startDate: string | undefined
    let endDate: string | undefined

    if (selectedPeriod === 'custom' && customDateRange) {
      startDate = customDateRange.start
      endDate = customDateRange.end
    } else if (selectedPeriod !== 'custom') {
      const end = new Date()
      const start = new Date()
      switch (selectedPeriod) {
        case 'day':
          start.setDate(start.getDate() - 1)
          break
        case 'week':
          start.setDate(start.getDate() - 7)
          break
        case 'month':
          start.setDate(start.getDate() - 30)
          break
        case '60days':
          start.setDate(start.getDate() - 60)
          break
        case '90days':
          start.setDate(start.getDate() - 90)
          break
      }
      startDate = start.toISOString().split('T')[0]
      endDate = end.toISOString().split('T')[0]
    }

    loadPartners(
      String(selectedCustomer.id),
      debouncedSearch || undefined,
      statusFilter !== 'all' ? (statusFilter as any) : undefined,
      categoryFilter !== 'all' ? categoryFilter : undefined,
      tierFilter !== 'all' ? tierFilter : undefined,
      currentPage,
      itemsPerPage,
      startDate,
      endDate
    )
  }, [loadPartners, selectedCustomer, debouncedSearch, statusFilter, categoryFilter, tierFilter, currentPage, itemsPerPage, selectedPeriod, customDateRange])

  useEffect(() => {
    if (error) {
      showError(error)
    }
  }, [error, showError])

  // Sort partners client-side
  const sortedPartners = useMemo(() => {
    const sorted = [...partners].sort((a, b) => {
      let comparison = 0
      switch (sortBy) {
        case 'name':
          comparison = a.name.toLowerCase().localeCompare(b.name.toLowerCase())
          break
        case 'value':
          comparison = valueDelivered(a.metrics) - valueDelivered(b.metrics)
          break
      }
      return sortOrder === 'asc' ? comparison : -comparison
    })
    return sorted
  }, [partners, sortBy, sortOrder])

  // Bars are scaled to the largest sponsor on the page, so length compares
  // sponsors and the colour split shows each one's mix.
  const maxValue = useMemo(
    () => Math.max(...partners.map((p) => valueDelivered(p.metrics)), 1e-9),
    [partners]
  )
  // The broadcast colour and legend only appear when some sponsor here has
  // broadcast exposure: a customer without broadcast analysis gets a plain
  // social bar rather than a legend entry that is always empty.
  const hasBroadcast = useMemo(
    () => partners.some((p) => (p.metrics?.broadcasts ?? 0) > 0),
    [partners]
  )

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder(field === 'name' ? 'asc' : 'desc')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Sponsors</h2>
          <p className="text-sm text-gray-600">
            Manage sponsor relationships and track performance
          </p>
        </div>
        <button
          onClick={() => setIsAddDrawerOpen(true)}
          className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Sponsor
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search sponsors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Category Filter */}
          <div className="relative">
            <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value)
                setCurrentPage(1)
              }}
              className="pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="all">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Tier Filter */}
          <select
            value={tierFilter}
            onChange={(e) => {
              setTierFilter(e.target.value)
              setCurrentPage(1)
            }}
            className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
          >
            <option value="all">All Tiers</option>
            {tiers.map((tier) => (
              <option key={tier.id} value={tier.id}>
                {tier.name}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setCurrentPage(1)
            }}
            className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
          >
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Pending">Pending</option>
            <option value="Suspended">Suspended</option>
          </select>

          {/* Period Filter */}
          <DateRangePicker
            value={customDateRange}
            onChange={handleDateRangeChange}
            maxRetentionDays={permissions?.effectiveLimits?.post_retention_days || 90}
            disabled={!selectedCustomer?.id}
            selectedPeriod={selectedPeriod}
          />
        </div>
      </div>

      {/* Partners Table */}
      <Card>
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-gray-500 mt-2">Loading sponsors...</p>
          </div>
        ) : partners.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {searchQuery || statusFilter !== 'all' || categoryFilter !== 'all' || tierFilter !== 'all'
                ? 'No sponsors match your filters'
                : 'No sponsors found. Add your first sponsor to get started.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th
                    className="px-3 py-2 text-left cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                        Sponsor
                      </span>
                      <ChevronDown
                        className={`h-3 w-3 text-gray-400 transition-transform ${
                          sortBy === 'name'
                            ? sortOrder === 'desc'
                              ? 'rotate-180'
                              : ''
                            : 'opacity-0'
                        }`}
                      />
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left">
                    <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                      Category
                    </span>
                  </th>
                  <th className="px-3 py-2 text-left">
                    <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                      Tier
                    </span>
                  </th>
                  <th className="px-3 py-2 text-left">
                    <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </span>
                  </th>
                  <th
                    className="px-3 py-2 text-left cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('value')}
                  >
                    {/* Title row, then the legend stacked beneath it. */}
                    <div className="flex items-center space-x-1">
                      <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                        Value delivered
                      </span>
                      <Tooltip
                        content={hasBroadcast
                          ? 'Social is earned media value from engagement on matched posts. Broadcast is ad equivalency: screen time priced as 30-second spots at each broadcast\'s rate. Two methods, shown together as one total. Bar length compares sponsors; the colours show the mix.'
                          : 'Earned media value from engagement on matched posts. Bar length compares sponsors.'}
                        placement="top"
                        maxWidth={280}
                      >
                        <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                      </Tooltip>
                      <ChevronDown
                        className={`h-3 w-3 text-gray-400 transition-transform ${
                          sortBy === 'value'
                            ? sortOrder === 'desc'
                              ? 'rotate-180'
                              : ''
                            : 'opacity-0'
                        }`}
                      />
                    </div>
                    {hasBroadcast && (
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-500">
                        <span className="inline-flex items-center gap-1">
                          <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: SOCIAL_COLOR }} />Social
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: BROADCAST_COLOR }} />Broadcast
                        </span>
                      </div>
                    )}
                  </th>
                  <th className="px-3 py-2 text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedPartners.map((partner) => {
                  const proxyLogoUrl = partner.logo_url && selectedCustomer?.id
                    ? getPartnerLogoUrl(partner.id, String(selectedCustomer.id), partner.updated_at)
                    : null

                  return (
                  <tr
                    key={partner.id}
                    onClick={() => {
                      setSelectedPartner(partner)
                      setIsDetailDrawerOpen(true)
                    }}
                    className="hover:bg-blue-50 cursor-pointer transition-colors"
                  >
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {partner.logo_url && selectedCustomer?.id ? (
                          <div className="w-8 h-8 rounded bg-white border border-gray-200 flex items-center justify-center">
                            <img
                              src={proxyLogoUrl!}
                              alt={partner.name}
                              className="w-7 h-7 object-contain"
                              crossOrigin="anonymous"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none'
                              }}
                            />
                          </div>
                        ) : (
                          <div className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center">
                            <span className="text-gray-500 text-xs font-semibold">
                              {partner.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="text-xs font-medium text-gray-900">{partner.name}</p>
                          {partner.website_url && (
                            <a
                              href={partner.website_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5"
                            >
                              Website
                              <ExternalLink className="h-2.5 w-2.5" />
                            </a>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <p className="text-xs text-gray-900">{partner.partner_categories.name}</p>
                    </td>
                    <td className="px-3 py-2">
                      <p className="text-xs text-gray-900">
                        {partner.partner_tiers?.name || '-'}
                      </p>
                    </td>
                    <td className="px-3 py-2"><StatusBadge status={partner.status} /></td>
                    <td className="px-3 py-2">
                      <ValueDeliveredBar metrics={partner.metrics} max={maxValue} showBroadcast={hasBroadcast} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <Tooltip content="View details" placement="left">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedPartner(partner)
                              setIsDetailDrawerOpen(true)
                            }}
                            className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Edit sponsor" placement="left">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedPartner(partner)
                              setIsEditDrawerOpen(true)
                            }}
                            className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {pagination && pagination.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
            <div className="flex items-center gap-2">
              <label htmlFor="itemsPerPage" className="text-sm text-gray-600">
                Show:
              </label>
              <select
                id="itemsPerPage"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value))
                  setCurrentPage(1) // Reset to first page when changing items per page
                }}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-600">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                {Math.min(currentPage * itemsPerPage, pagination.totalPartners)} of{' '}
                {pagination.totalPartners} sponsors
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={!pagination.hasPrevPage}
                className={`p-2 rounded-lg transition-colors ${
                  pagination.hasPrevPage
                    ? 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>

              <div className="flex items-center gap-1">
                {/* Show page numbers */}
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    // Show first page, last page, current page, and pages around current
                    return (
                      page === 1 ||
                      page === pagination.totalPages ||
                      Math.abs(page - currentPage) <= 1
                    )
                  })
                  .map((page, index, array) => {
                    // Add ellipsis if there's a gap
                    const prevPage = array[index - 1]
                    const showEllipsis = prevPage && page - prevPage > 1

                    return (
                      <React.Fragment key={page}>
                        {showEllipsis && (
                          <span className="px-2 text-gray-400">...</span>
                        )}
                        <button
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                            page === currentPage
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                          }`}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    )
                  })}
              </div>

              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={!pagination.hasNextPage}
                className={`p-2 rounded-lg transition-colors ${
                  pagination.hasNextPage
                    ? 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Add Partner Drawer */}
      <AddPartnerDrawer isOpen={isAddDrawerOpen} onClose={() => setIsAddDrawerOpen(false)} />

      {/* Edit Partner Drawer */}
      <EditPartnerDrawer
        isOpen={isEditDrawerOpen}
        onClose={() => {
          setIsEditDrawerOpen(false)
          setSelectedPartner(null)
        }}
        partner={selectedPartner}
      />

      {/* Partner Detail Drawer */}
      <PartnerDetailDrawer
        key={selectedPartner?.id || 'none'}
        isOpen={isDetailDrawerOpen}
        onClose={() => {
          setIsDetailDrawerOpen(false)
          setSelectedPartner(null)
        }}
        partner={selectedPartner}
      />

    </div>
  )
}

export default PartnersList
